//Importing Key Components
import { Form } from "../form/FormComponents"
import LineElement from "./LineElement"
import IconButton from "../generic/IconButton"
import { FlexBox } from "../generic/Layout"
import { LinkText } from "../generic/Core"
import { Icon_Plus } from "../../styles/icons"
import runLogic from "../../../../common/logic/runLogic"
import useTranslation from "../../hooks/useTranslation"
import { getNestedValue, setNestedValue } from "../../utils/objects/nestedValue"
import useAlert from "../../hooks/useAlert"

import { P } from "../generic/Core"

import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core"
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers"

const AddItem = ({ section, clickAction, testId }) => {
    const { t } = useTranslation()

    return (
        <FlexBox style={{alignItems: 'center'}}>
            <IconButton icon={Icon_Plus} $rounded $variant='primary' $size='2rem' clickAction={clickAction} testId={testId} />
            <LinkText onClick={clickAction}>{t(section.meta.linesAddAction)}</LinkText>
        </FlexBox>
    )
}

const CardViewFormLines = ({ toggleExpanded, section, dispatch, validated, entity, useStore, linesArray, path = [] , options}) => {
    const { t } = useTranslation()
    const {AlertComponent, showAlert} = useAlert()

    // Get full state and current context state
    const [state, setState] = useStore(s => getNestedValue(s, path))

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
    )

    const toggleSection = () => {
        toggleExpanded(section.id)
    }

    const addItem = () => {
        const newLines = [...(Array.isArray(state[linesArray]) ? state[linesArray] : []), {}]
        setState({ [linesArray]: newLines }, { path })
    }

    const deleteLine = (idx) => {
        // The condition can prevent a line from being deleted when true.
        const condition = state[linesArray][idx][section.meta?.preventDelete?.field] > 0 || false
        if (section.meta?.preventDelete && condition) {
            showAlert('inventory.shipment.line.prevent.delete.title', 'inventory.shipment.line.prevent.delete.body', 'warning')
            return
        }

        const newLines = state[linesArray].filter((_, i) => i !== idx)
        const newData = { ...state, [linesArray]: newLines }
        const processedData = runLogic(newData, state.entity)
        toggleSection()
        setState(processedData, { path })
    }

    const localDispatch = (action, data, idx) => {
        if (action === 'delete') {
            deleteLine(idx)
            setState({ savedOn: new Date() })

        } else {
            dispatch(action, data)
        }
    }

    const handleDragEnd = (event) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const lines = state[linesArray] || []
        const oldIndex = lines.findIndex((_, i) => `line-${i}` === active.id)
        const newIndex = lines.findIndex((_, i) => `line-${i}` === over.id)
        if (oldIndex === -1 || newIndex === -1) return

        const reordered = arrayMove(lines, oldIndex, newIndex)
        setState({ [linesArray]: reordered, savedOn: new Date() }, { path })
    }

    const lines = Array.isArray(state[linesArray]) ? state[linesArray] : []
    const sortableIds = lines.map((_, i) => `line-${i}`)

    return (
        <>
        <Form.Section name='FormSection' width={section.meta.width}>
            {section.meta.sectionTitle && (
                <P style={{ fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.875rem' }}>
                    {t(section.meta.sectionTitle)}
                </P>
            )}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                    {
                        lines.map(
                            (line, i) => <LineElement
                                key={line.id || line._key || i}
                                sortableId={`line-${i}`}
                                idx={i}
                                dispatch={localDispatch}
                                section={section}
                                validated={validated}
                                toggleSection={toggleSection}
                                entity={entity}
                                useStore={useStore}
                                linesArray={linesArray}
                                path={path}
                                options={options}
                            />
                        )
                    }
                </SortableContext>
            </DndContext>
            {section.meta.showAddLine !== false && !section.meta.displayOnly && <AddItem section={section} clickAction={addItem} testId={`${section.id}.add.line`}></AddItem>}
        </Form.Section>
        {AlertComponent}
        </>
    )
}

export default CardViewFormLines
